/**
 * Performance configuration for development and production
 */

// Debounce configuration
export const DEBOUNCE_DELAYS = {
  api_calls: 300,
  user_input: 150,
  search: 500,
  auto_save: 1000,
} as const;

// Request deduplication settings
export const DEDUPLICATION_CONFIG = {
  enabled: false, // Temporarily disabled to fix image loading issues
  timeout: 200, // ms (reduced from 1000)
  max_concurrent_requests: 3,
} as const;

// Cache busting configuration
export const CACHE_CONFIG = {
  // Disable aggressive caching in development
  force_reload: process.env.NODE_ENV === 'development',
  
  // Cache durations (ms)
  short_cache: 1 * 60 * 1000,   // 1 minute
  medium_cache: 5 * 60 * 1000,  // 5 minutes
  long_cache: 30 * 60 * 1000,   // 30 minutes
} as const;

// Request tracking for debugging
class RequestTracker {
  private requests = new Map<string, { count: number; lastCall: number }>();
  
  track(url: string): boolean {
    const now = Date.now();
    const key = url.split('?')[0] || url; // Remove query params for grouping, fallback to url
    
    const existing = this.requests.get(key);
    
    if (existing) {
      // Prevent calls within 200ms of each other (reduced from 1000ms)
      // This allows legitimate React double-mounting while preventing rapid duplicates
      if (now - existing.lastCall < 200) {
        console.warn(`⚠️ Duplicate API call prevented: ${key} (${existing.count + 1} attempts)`);
        return false;
      }
      
      existing.count++;
      existing.lastCall = now;
    } else {
      this.requests.set(key, { count: 1, lastCall: now });
    }
    
    return true;
  }
  
  getStats(): Record<string, number> {
    const stats: Record<string, number> = {};
    this.requests.forEach((value, key) => {
      stats[key] = value.count;
    });
    return stats;
  }
  
  reset(): void {
    this.requests.clear();
  }
}

export const requestTracker = new RequestTracker();

// Development debugging
if (process.env.NODE_ENV === 'development') {
  // Log request stats every 30 seconds
  setInterval(() => {
    const stats = requestTracker.getStats();
    const duplicates = Object.entries(stats).filter(([, count]) => count > 3);
    
    if (duplicates.length > 0) {
      console.group('🔍 API Call Analysis');
      console.log('Requests with multiple calls:');
      duplicates.forEach(([url, count]) => {
        console.log(`  ${url}: ${count} calls`);
      });
      console.groupEnd();
    }
  }, 30000);
}

// Request interceptor for development
export const interceptRequest = (url: string): boolean => {
  if (!DEDUPLICATION_CONFIG.enabled) {
    return true;
  }
  
  return requestTracker.track(url);
};
